import { PickerDraftKind, type Span } from '@wiztime/core';
import { readSession, removeSession, writeSession } from '../../../lib/safeStorage';
import { SessionKey } from '../../../lib/storageKeys';

/**
 * The session key `wiztime.pickerDraft` (data dictionary §7.1): the picker's content stashed
 * when the page goes hidden (2026-10b §5.4). Per tab, never synchronised. Entries older than
 * `PICKER_DRAFT_MAX_AGE_MS` are dropped on read.
 */

export const PICKER_DRAFT_MAX_AGE_MS = 30 * 60 * 1000;

/** A stashed value together with the baseline it was edited from (overwrite protection). */
export interface FieldBackup<T> {
  readonly value: T;
  readonly baseline: T;
}

/** Create context: the record does not exist yet; the whole input is the draft. */
export interface CreatePickerDraft {
  readonly kind: typeof PickerDraftKind.Create;
  readonly date: string;
  readonly span: Span;
  readonly title: string;
  readonly note: string;
  readonly savedAt: number;
}

/**
 * Existing-block context: a backstop for the commit fired on page hide, in case the async
 * IndexedDB write never completed. Each dirty field carries its value and the baseline it was
 * committed over; restoring only writes a field whose stored value still equals the baseline.
 */
export interface ExistingPickerDraft {
  readonly kind: typeof PickerDraftKind.Existing;
  readonly date: string;
  readonly blockIds: readonly string[];
  readonly fields: {
    readonly title?: FieldBackup<string | null>;
    readonly note?: FieldBackup<string | null>;
    readonly span?: FieldBackup<Span>;
  };
  readonly savedAt: number;
}

export type PickerDraft = CreatePickerDraft | ExistingPickerDraft;

export function savePickerDraft(draft: PickerDraft): void {
  writeSession(SessionKey.PickerDraft, JSON.stringify(draft));
}

export function clearPickerDraft(): void {
  removeSession(SessionKey.PickerDraft);
}

function isSpan(value: unknown): value is Span {
  return (
    typeof value === 'object' &&
    value !== null &&
    typeof (value as Span).startMin === 'number' &&
    typeof (value as Span).endMin === 'number'
  );
}

/**
 * Read the stashed draft; an expired, malformed or unreadable entry is removed and reported
 * as "none". The caller decides whether the date and picker state allow restoring.
 */
export function loadPickerDraft(nowMs: number): PickerDraft | null {
  const raw = readSession(SessionKey.PickerDraft);
  if (raw === null) return null;
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    clearPickerDraft();
    return null;
  }
  const draft = parsed as Partial<PickerDraft>;
  const fresh =
    typeof draft.savedAt === 'number' &&
    draft.savedAt <= nowMs &&
    nowMs - draft.savedAt <= PICKER_DRAFT_MAX_AGE_MS;
  if (!fresh || typeof draft.date !== 'string') {
    clearPickerDraft();
    return null;
  }
  if (draft.kind === PickerDraftKind.Create) {
    const create = draft as Partial<CreatePickerDraft>;
    if (isSpan(create.span) && typeof create.title === 'string' && typeof create.note === 'string') {
      return create as CreatePickerDraft;
    }
  }
  if (draft.kind === PickerDraftKind.Existing) {
    const existing = draft as Partial<ExistingPickerDraft>;
    if (Array.isArray(existing.blockIds) && typeof existing.fields === 'object' && existing.fields !== null) {
      return existing as ExistingPickerDraft;
    }
  }
  clearPickerDraft();
  return null;
}
